// Calendar days, item keys and counts for the What's new page.
//
// Ship dates are plain calendar days ('YYYY-MM-DD') and are handled as days, never as instants:
// `new Date('2026-08-31')` is UTC midnight, which is still August 30 for every seller in the Americas.
// Days compare correctly as strings, so they stay strings until they are formatted.

const DAY_MS = 24 * 60 * 60 * 1000;

// A seller who joined this recently has seen too little shipped since then for "since you joined" to say
// much, so their summary covers the last 12 months instead.
const RECENT_SIGNUP_DAYS = 90;

// ----------------------------------------------------------------------

export function parseDay (day) {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date);
}

function toDay (date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function wholeDaysBetween (earlier, later) {
  return Math.round((parseDay(toDay(later)) - parseDay(toDay(earlier))) / DAY_MS);
}

function validDate (value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

export function formatShortDay (day) {
  return parseDay(day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatLongDay (day) {
  return parseDay(day).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

export function monthParts (month) {
  const first = parseDay(`${month}-01`);
  return {
    name: first.toLocaleDateString('en-US', { month: 'long' }),
    year: String(first.getFullYear()),
  };
}

// An update written without its own ship date counts as shipped on the last day of its month.
export function dayOf (item, month) {
  if (item.date) return item.date;
  const [year, monthNumber] = month.split('-').map(Number);
  return toDay(new Date(year, monthNumber, 0));
}

export function daysAgo (day, today = new Date()) {
  const days = wholeDaysBetween(parseDay(day), today);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

// ----------------------------------------------------------------------

// What a seller's browser remembers as seen. Every update has an id that never changes, so rewording or
// redating it later does not announce it again.
export function itemKey (entry, item) {
  return `${entry.month}/${item.id}`;
}

// Every update in a month, headliners first, each with its key and ship day.
export function entryItems (entry) {
  const withDetails = (isHeadliner) => (item) => ({
    ...item,
    key: itemKey(entry, item),
    day: dayOf(item, entry.month),
    month: entry.month,
    isHeadliner,
  });
  return [...entry.headliners.map(withDetails(true)), ...entry.updates.map(withDetails(false))];
}

export function countUpdates (entry) {
  return entry.headliners.length + entry.updates.length;
}

const kindOf = (item) => item.kind || 'improved';

// What shipped after `sinceDay`: the new marketplaces (by platform), new features and improvements.
export function summarize (entries, sinceDay) {
  const items = entries.flatMap(entryItems).filter((item) => item.day > sinceDay);
  return {
    marketplaces: items.filter((item) => kindOf(item) === 'marketplace').map((item) => item.platform),
    features: items.filter((item) => kindOf(item) === 'new').length,
    improvements: items.filter((item) => kindOf(item) === 'improved').length,
  };
}

export function newestDay (entries) {
  return entries
    .flatMap(entryItems)
    .reduce((newest, item) => (item.day > newest ? item.day : newest), '');
}

// ----------------------------------------------------------------------

export function signupDay (createdAt, today = new Date()) {
  const joined = validDate(createdAt);
  return toDay(joined || today);
}

export function summaryWindow (createdAt, today = new Date()) {
  const joined = validDate(createdAt);
  if (joined && wholeDaysBetween(joined, today) >= RECENT_SIGNUP_DAYS) {
    return {
      sinceDay: toDay(joined),
      label: `Since you joined in ${joined.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
    };
  }
  const yearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
  return { sinceDay: toDay(yearAgo), label: 'In the last 12 months' };
}

// Updates the seller has not seen yet, newest month first. Anything that shipped on or before the day they
// joined was already there when they arrived, so it is never new to them.
export function unseenItems (entries, seenKeys, joinedDay) {
  return entries
    .flatMap(entryItems)
    .filter((item) => item.day > joinedDay && !seenKeys.has(item.key));
}

// What the "New in Voolist" window announces: the newest month that has headliners the seller has not seen,
// those headliners, and how many of that month's smaller updates are new too. Smaller updates alone are not
// worth a window, so a month whose only news is smaller updates is skipped. Null when nothing qualifies.
export function announcement (entries, unseen) {
  const newest = unseen.find((item) => item.isHeadliner);
  if (!newest) return null;
  const fromThatMonth = unseen.filter((item) => item.month === newest.month);
  return {
    entry: entries.find((entry) => entry.month === newest.month),
    headliners: fromThatMonth.filter((item) => item.isHeadliner),
    moreUpdates: fromThatMonth.filter((item) => !item.isHeadliner).length,
  };
}
